import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "store";
import { fetchPatientList } from "store/middleware";
import { log, LogLevel } from "utils/LogUtil";

/**
 * 애플리케이션이 시작될 때 자동으로 필요한 데이터를 로드하는 컴포넌트
 * App 컴포넌트를 감싸서 사용합니다.
 */
const DataInitializer: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const dispatch = useDispatch<AppDispatch>();
  const { isAuthenticated, user, isLoading } = useSelector((state: RootState) => state.user);

  useEffect(() => {
    log(LogLevel.VERBOSE, "[DataInitializer] 상태 변경 감지:", {
      isAuthenticated,
      user,
      isLoading,
      timestamp: new Date().toISOString(),
    });

    // 인증된 사용자인 경우에만 데이터를 로드합니다
    if (isAuthenticated && user) {
      log(LogLevel.INFO, "[DataInitializer] 인증된 사용자 감지: 초기 데이터 로드 시작", {
        userId: user.id,
        userRoles: user.roles,
        timestamp: new Date().toISOString(),
      });

      // 비동기 작업을 async 함수로 처리
      const loadData = async () => {
        try {
          const result = await dispatch(
            fetchPatientList({ forceRefresh: true })
          ).unwrap();
          log(LogLevel.INFO, "[DataInitializer] 초기 데이터 로드 완료", {
            userId: user.id,
            patientCount: result?.length || 0,
            timestamp: new Date().toISOString(),
          });
        } catch (error) {
          log(LogLevel.ERROR, "[DataInitializer] 초기 데이터 로드 실패:", {
            error,
            userId: user.id,
            timestamp: new Date().toISOString(),
          });
        }
      };

      // 비동기 함수 실행
      loadData();
    } else {
      log(LogLevel.INFO, "[DataInitializer] 인증되지 않은 상태 또는 사용자 정보 없음:", {
        isAuthenticated,
        hasUser: !!user,
        timestamp: new Date().toISOString(),
      });
    }
  }, [dispatch, isAuthenticated, user, isLoading]);

  return <>{children}</>;
};

export default DataInitializer;
